// Putting a Marvelous garment on the avatar — the three.js half.
//
// The pure halves decide everything that can be decided without a scene:
// what unit the file is in (garmentUnits.js), whether it belongs on this body
// (garmentFit.js), and which bones each vertex should ride (garmentBind.js).
// This module is the glue: load the glTF, run those three, and hand the result
// to three as ordinary SkinnedMeshes sharing the RIG'S OWN Skeleton.
//
// Sharing the skeleton is the whole design. three then skins the garment in
// the same pass it skins the body: no postMixer lane, no per-frame JS, no
// second clock, and — unlike the ML cloth deformer next door, whose README
// records that its cloth does not follow body root translation — it cannot
// desync when the character walks, because there is nothing to keep in sync.
//
// A garment comes from one of two places, and `source` says which. Normally it
// is the character's own `garment_assets` row, fetched through the credentialed
// API client because the loader's own fetch carries no auth. The local
// `public/garments/` directory survives as a dev affordance — dropping a file in
// a folder beats a round trip while iterating — and a garment loaded that way
// will vanish on reload, which is why `persisted` follows the source and the
// pill says so (GarmentIndicator). Claiming either way wrongly is a lie the
// user acts on: "not saved" about a stored asset makes them re-upload it, and
// the reverse loses their work.
//
// Debug seam: `window.__GARMENT__`.

import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js'
import { fetchAamFile } from '../assetManagerApi.js'
import { collectSkinTargets, mergeCompatibleSkins, boneSkinMatrix } from '../skinTargets.js'
import { topInfluences, VertexGrid } from '../skinBind.js'
import { inferUnitScale } from './garmentUnits.js'
import { fitReport, BODY_LANDMARK_JOINTS, pickLandmarkBone } from './garmentFit.js'
import { bindGarment, GARMENT_INFLUENCES, GARMENT_MAX_BIND_DIST } from './garmentBind.js'
import { coveredBodyVertices, cullTriangles } from './garmentCull.js'
import { DEFAULT_MESH_SLOTS } from '../characterAuthoringStore.js'

const _v = new THREE.Vector3()
const _m = new THREE.Matrix4()

/** `idle | loading | ready | error`, plus everything the report carries. */
let _state = { phase: 'idle' }
const _listeners = new Set()
let _attached = null   // { group, hidden: THREE.Mesh[] }

function setState(next) {
  _state = next
  for (const fn of _listeners) { try { fn(_state) } catch (e) { console.error('[garment] listener threw', e) } }
}

/** Current garment state. Not React state — the loader runs outside React. */
export function garmentState() { return _state }

/** Subscribe to state changes; returns an unsubscribe. */
export function onGarmentChange(fn) {
  _listeners.add(fn)
  return () => _listeners.delete(fn)
}

/**
 * SKIN-SPACE matrix element `e` of the landmark bone `names` selects — the same
 * space `collectSkinTargets` reports and the garment is measured in, never
 * `bone.matrixWorld`. `pickLandmarkBone` scans the CANDIDATES in order, not the
 * bones — see its header for the calf/foot inversion that rule exists to
 * prevent.
 */
function jointElement(skeleton, names, e) {
  const i = pickLandmarkBone(skeleton.bones, names)
  if (i < 0) return null
  return boneSkinMatrix(skeleton, i, _m).elements[e]
}

/** Skin-space Y of the first joint in `names` that the skeleton has. */
function jointY(skeleton, names) { return jointElement(skeleton, names, 13) }

/** Skin-space |X| of the first joint in `names`. */
function jointHalfX(skeleton, names) {
  const x = jointElement(skeleton, names, 12)
  return x == null ? null : Math.abs(x)
}

/**
 * A scattering of the garment's vertices in rig-world space, for the skin
 * choice below. Strided across every piece so a 36-piece garment is judged by
 * all of it, not by whichever piece happens to come first.
 */
function samplePoints(pieces, scale, budget) {
  const total = pieces.reduce((n, p) => n + p.geometry.attributes.position.count, 0)
  const step = Math.max(1, Math.floor(total / budget))
  const out = []
  for (const piece of pieces) {
    const pos = piece.geometry.attributes.position
    for (let i = 0; i < pos.count; i += step) {
      _v.fromBufferAttribute(pos, i).applyMatrix4(piece.matrixWorld).multiplyScalar(scale)
      out.push(_v.x, _v.y, _v.z)
    }
  }
  return Float32Array.from(out)
}

/**
 * Which of the rig's skins is this garment actually ON?
 *
 * Two wrong answers were measured on aoi-fridaytest before this one, and both
 * rendered plausibly enough to be missed without the report:
 *
 *   - THE BIGGEST SKIN. His rig carries five separate skeletons (body, his own
 *     tee, his jeans, his loafers, and a face/hair rig of 875 bones), and the
 *     largest by vertex count is the face and hair. A tee-and-joggers bound to
 *     the scalp came out 68% unbound, worst bind 129 cm.
 *   - THE NEAREST SKIN ON AVERAGE. That picks his existing crewneck tee, which
 *     the new garment is lying directly on top of — and which covers only his
 *     torso, so every jogger vertex was orphaned: 25% unbound, worst bind 81 cm.
 *
 * COVERAGE is the question that actually matters: of the garment, how much of
 * it has THIS skin underneath? A torso-only skin scores ~0.6 against a whole
 * body's ~1.0 however close it is. Mean distance survives only as the
 * tie-break, for two skins that both cover everything.
 *
 * @returns {{ target: object, coverage: number, meanDist: number,
 *             scores: Array<{meshes: string[], coverage: number, meanDist: number}> }}
 */
function chooseSkinTarget(targets, points, maxDist) {
  const n = Math.floor(points.length / 3)
  const step = Math.max(1, Math.floor(n / 256))
  const scores = []
  for (const t of targets) {
    let sum = 0, count = 0, covered = 0
    for (let i = 0; i < n; i += step, count++) {
      t.grid.nearest(points[i * 3], points[i * 3 + 1], points[i * 3 + 2])
      const d = t.grid.lastDist
      sum += Math.min(d, 1)
      if (d <= maxDist) covered++
    }
    scores.push({
      target: t,
      meshes: t.meshes.map((m) => m.name),
      coverage: count ? covered / count : 0,
      meanDist: count ? sum / count : Infinity,
    })
  }
  scores.sort((a, b) => (b.coverage - a.coverage) || (a.meanDist - b.meanDist))
  const best = scores[0]
  return {
    target: best.target,
    coverage: best.coverage,
    meanDist: best.meanDist,
    scores: scores.map(({ meshes, coverage, meanDist }) => ({ meshes, coverage, meanDist })),
  }
}

/**
 * The rig's own outfit meshes, by the character's mesh-slot patterns — the
 * same `outfit` role the character wizard already uses. A garment REPLACES
 * these: drawn together, the character's own shirt intersects the new one and
 * the result reads as a broken importer. Hidden, not removed, so the outliner
 * and the material mapping still see them and un-hiding restores the original.
 */
function hideOwnOutfit(root, patterns) {
  const res = (patterns ?? DEFAULT_MESH_SLOTS.outfit ?? []).map((p) => new RegExp(p, 'i'))
  const hidden = []
  root.traverse((o) => {
    if (!o.isMesh || o.userData?.isGarment) return
    if (res.some((re) => re.test(o.name))) { o.visible = false; hidden.push(o) }
  })
  return hidden
}

/**
 * Hide the skin the garment covers, by removing whole triangles from the body
 * meshes' index buffers.
 *
 * A garment and the skin under it are two surfaces a few millimetres apart,
 * skinned by the same joints but DIFFERENT weights, so they do not deform
 * identically and the skin comes through wherever its answer lands outside the
 * cloth's. Unreal solves it by not drawing that skin, which is why a MetaHuman
 * published WITH clothes already arrives culled — and why a body-only export
 * like isaac-body-marvelous shows the poke at the knees and shins while
 * aoi-fridaytest never did.
 *
 * The original index is kept on the mesh so `disposeGarment` can put the skin
 * back: the cull belongs to the garment, not to the character.
 *
 * @returns {{ meshes: THREE.Mesh[], removed: number, total: number }}
 */
function cullCoveredSkin(target, pieces) {
  // One grid over every garment vertex, with its world-space normal alongside.
  let total = 0
  for (const p of pieces) total += p.geometry.attributes.position.count
  const pos = new Float32Array(total * 3)
  const nrm = new Float32Array(total * 3)
  let k = 0
  for (const p of pieces) {
    const a = p.geometry.attributes
    // The geometry was baked into the rig's SKIN space at attach, and
    // applyMatrix4 carries the normals with it, so both are already in that
    // same space here — which is what the body vertices below are read in too.
    for (let i = 0; i < a.position.count; i++, k++) {
      pos[k * 3] = a.position.getX(i); pos[k * 3 + 1] = a.position.getY(i); pos[k * 3 + 2] = a.position.getZ(i)
      if (a.normal) {
        nrm[k * 3] = a.normal.getX(i); nrm[k * 3 + 1] = a.normal.getY(i); nrm[k * 3 + 2] = a.normal.getZ(i)
      }
    }
  }
  if (!pieces.every((p) => p.geometry.attributes.normal)) {
    console.warn('[garment] a piece has no NORMAL, so "inside the cloth" cannot be decided — skin not culled')
    return { meshes: [], removed: 0, total: 0 }
  }
  const grid = new VertexGrid(pos)

  const meshes = []
  let removed = 0, tris = 0
  for (const m of target.meshes) {
    if (m.userData?.isGarment || m.userData?.isHairStrands) continue
    const p = m.geometry.attributes.position
    const world = new Float32Array(p.count * 3)
    for (let i = 0; i < p.count; i++) {
      _v.fromBufferAttribute(p, i).applyMatrix4(m.bindMatrix)
      world[i * 3] = _v.x; world[i * 3 + 1] = _v.y; world[i * 3 + 2] = _v.z
    }
    const covered = coveredBodyVertices({ bodyPositions: world, grid, garmentNormals: nrm })
    const r = cullTriangles(m.geometry.index ? m.geometry.index.array : null, covered)
    tris += r.kept + r.removed
    if (r.index == null) continue
    removed += r.removed
    // Keep the original so the skin comes back when the garment goes.
    if (m.userData.garmentCullOriginalIndex === undefined) {
      m.userData.garmentCullOriginalIndex = m.geometry.index ?? null
    }
    m.geometry.setIndex(new THREE.BufferAttribute(r.index, 1))
    meshes.push(m)
  }
  return { meshes, removed, total: tris }
}

/**
 * Load a garment and skin it onto `root`'s skeleton.
 *
 * Not awaited by the caller: the avatar is complete without a garment, and a
 * garment that fails must report itself rather than hold the load — the same
 * rule `loadFaceRigForCharacter` follows.
 *
 * @param {THREE.Object3D} root      the loaded rig (AvatarModel's `combined`)
 * @param {object} args
 * @param {string} args.url          where to fetch the glTF
 * @param {string} args.name         what the user asked for, for messages
 * @param {string} [args.dracoPath]  decoder directory, as AvatarModel uses
 * @param {boolean} [args.hideOutfit] hide the rig's own outfit meshes; default true
 * @param {boolean} [args.cullSkin]   hide body triangles the garment covers; default true
 * @param {'asset'|'local'} [args.source]  where the URL came from — a server row, or the
 *   local dev directory. Decides whether the pill says 'not saved'.
 * @param {string[]} [args.outfitPatterns]  the character's own outfit slot patterns
 * @param {string} [args.unit]       force the garment's unit instead of inferring
 * @param {boolean} [args.applyFit]  apply the advisory fit transform; default FALSE
 * @returns {Promise<object>} the same object `garmentState()` then returns
 */
export async function loadGarment(root, {
  url, name, dracoPath = '/draco/', hideOutfit = true, outfitPatterns = null,
  unit = undefined, applyFit = false, cullSkin = true, source = 'local', assetId = null,
}) {
  disposeGarment(root)
  setState({ phase: 'loading', name, url })
  try {
    // Fuse the interchangeable copies first: a published MetaHuman keeps its
    // torso and legs on its CLOTHING meshes (the body's under-clothing skin is
    // culled), each on its own copy of the same skeleton, so the whole body
    // surface only exists as their union. See mergeCompatibleSkins.
    const targets = mergeCompatibleSkins(
      collectSkinTargets(root, { skip: (o) => o.userData?.isHairStrands || o.userData?.isGarment }),
    )
    if (targets.length === 0) {
      throw new Error('this character has no skinned mesh to bind a garment to '
        + '(a splat character whose bundle ships no rig.glb has no body skeleton at all)')
    }
    const loader = new GLTFLoader()
    const draco = new DRACOLoader()
    draco.setDecoderPath(dracoPath)
    loader.setDRACOLoader(draco)
    // A garment served by the asset manager sits behind credentials, and the
    // loader's own fetch carries none — in dev it also needs the AAM base
    // prefix, since VITE_ASSET_MANAGER_URL is a proxy path rather than an
    // origin. So an /api/ URL is fetched through the API client and PARSED,
    // while a local dev file under public/garments/ loads directly.
    //
    // parse() with an empty resourcePath is right for a .glb, which is
    // self-contained. A .gltf keeps its geometry in a sibling .bin that this
    // path cannot resolve, so it is REFUSED rather than half-loaded into a
    // mesh with no vertices.
    let gltf
    if (/\.gltf$/i.test(url)) {
      // A .gltf keeps its geometry in a sibling .bin, which neither path here
      // resolves. Refused rather than half-loaded into a mesh with no vertices.
      throw new Error('a garment must be a .glb — a .gltf keeps its geometry in a sibling .bin, '
        + 'which this loader cannot resolve. Re-export from Marvelous as .glb (binary).')
    }
    const res = url.startsWith('/api/') ? await fetchAamFile(url) : await fetch(url)
    if (!res.ok) throw new Error(`fetching the garment failed: HTTP ${res.status} for ${url}`)
    const buf = await res.arrayBuffer()
    // A dev server answers a MISSING file with the SPA's index.html rather than
    // a 404, so an absent garment reached the parser as HTML and surfaced as
    // `Unexpected token '<'` — a message about JSON, for a missing mesh. Check
    // the glTF magic and say what actually happened.
    if (buf.byteLength < 4 || new DataView(buf).getUint32(0, true) !== 0x46546c67) {
      throw new Error(`${url} is not a .glb — the server returned ${buf.byteLength} bytes that do not `
        + 'start with the glTF magic (a dev server answers a missing file with index.html).')
    }
    gltf = await new Promise((resolve, reject) => loader.parse(buf, '', resolve, reject))
    draco.dispose()

    // Everything the file draws, flattened. Marvelous writes one mesh per
    // pattern piece on a "multi object" export and one mesh of many primitives
    // on a "single object" export; three gives us a Mesh per primitive either
    // way, so both shapes arrive here identically.
    const pieces = []
    gltf.scene.updateMatrixWorld(true)
    gltf.scene.traverse((o) => { if (o.isMesh && o.geometry?.attributes?.position) pieces.push(o) })
    if (pieces.length === 0) throw new Error('the file contains no mesh')

    // --- units -------------------------------------------------------------
    const box = new THREE.Box3().setFromObject(gltf.scene)
    const units = inferUnitScale(box.max.y - box.min.y, unit ? { unit } : {})
    console.info(`[garment] "${name}": ${units.reason}`)

    // --- which skin? -------------------------------------------------------
    // Decided from the garment's own position, which means it has to wait until
    // the garment is loaded and scaled. Sampled at the unit scale alone: the
    // advisory fit offset is off by default, and a garment that needs it is a
    // mismatch whose skin choice this would not change.
    const sample = samplePoints(pieces, units.scale, 512)
    const { target, coverage, meanDist, scores } = chooseSkinTarget(targets, sample, GARMENT_MAX_BIND_DIST)
    const skeleton = target.skeleton
    console.info(`[garment] "${name}": binding to the skin of ${target.meshes.map((m) => m.name).join(', ')} `
      + `(${skeleton.bones.length} bones, covers ${(coverage * 100).toFixed(0)}% of the garment, `
      + `mean distance ${(meanDist * 100).toFixed(1)} cm). Skins considered: `
      + scores.map((s) => `${s.meshes[0]}${s.meshes.length > 1 ? `+${s.meshes.length - 1}` : ''} ${(s.coverage * 100).toFixed(0)}%`).join(', '))

    // --- fit ---------------------------------------------------------------
    const body = {
      ankleY: jointY(skeleton, BODY_LANDMARK_JOINTS.ankle),
      neckY: jointY(skeleton, BODY_LANDMARK_JOINTS.neck),
      shoulderHalfX: jointHalfX(skeleton, BODY_LANDMARK_JOINTS.shoulder) ?? 0,
    }
    const haveLandmarks = body.ankleY != null && body.neckY != null
    const fit = haveLandmarks
      ? fitReport({
          garment: { minY: box.min.y * units.scale, maxY: box.max.y * units.scale, halfX: Math.max(Math.abs(box.min.x), Math.abs(box.max.x)) * units.scale },
          body,
        })
      : { verdict: 'unknown', spanRatio: null, hemOffset: 0, widthRatio: null, suggested: { scale: 1, offsetY: 0 }, summary: `cannot judge fit: this skeleton has none of ${BODY_LANDMARK_JOINTS.neck.join('/')} or ${BODY_LANDMARK_JOINTS.ankle.join('/')}` }
    const fitLog = fit.verdict === 'fits' ? console.info : console.warn
    fitLog(`[garment] "${name}": ${fit.summary}`)

    // The advisory transform is applied ONLY on request. A garment that is the
    // wrong size must LOOK wrong by default, or nobody learns it was authored
    // for another figure (garmentFit.js).
    const extra = applyFit ? fit.suggested : { scale: 1, offsetY: 0 }
    const scale = units.scale * extra.scale
    if (applyFit) console.warn(`[garment] "${name}": ?garmentfit=1 — applying the advisory transform (x${extra.scale.toFixed(4)}, +${extra.offsetY.toFixed(4)} m)`)

    // --- bind --------------------------------------------------------------
    // The grid is in the skeleton's SKIN space (skinTargets.js), which for a
    // glTF character is the raw geometry — a character standing at the origin
    // in metres, which is also how a Marvelous export is authored. So garment
    // vertices go local -> the file's own world -> scaled/placed, and land in
    // that same space with no rig transform in the way.
    const place = new THREE.Matrix4().makeTranslation(0, extra.offsetY, 0).multiply(new THREE.Matrix4().makeScale(scale, scale, scale))
    // A vertex the skin does not reach rides the joint nearest the garment's
    // centre rather than bone 0, which is the root and would fling it.
    const centre = box.getCenter(new THREE.Vector3()).applyMatrix4(place)
    let fallbackBone = 0, bestD = Infinity
    skeleton.bones.forEach((b, i) => {
      const e = boneSkinMatrix(skeleton, i, _m).elements
      const d = (e[12] - centre.x) ** 2 + (e[13] - centre.y) ** 2 + (e[14] - centre.z) ** 2
      if (d < bestD) { bestD = d; fallbackBone = i }
    })

    const group = new THREE.Group()
    group.name = `garment:${name}`
    group.userData.isGarment = true
    const perPiece = []
    let boundTotal = 0, unboundTotal = 0, worstDist = 0

    for (const piece of pieces) {
      const geometry = piece.geometry.clone()
      // Bake the file's own node transform plus scale/placement into the
      // geometry, so the SkinnedMesh sits at identity and its bindMatrix has
      // one less thing in it.
      geometry.applyMatrix4(_m.copy(place).multiply(piece.matrixWorld))
      const pos = geometry.attributes.position
      const positions = new Float32Array(pos.count * 3)
      for (let i = 0; i < pos.count; i++) {
        _v.fromBufferAttribute(pos, i)
        positions[i * 3] = _v.x; positions[i * 3 + 1] = _v.y; positions[i * 3 + 2] = _v.z
      }
      const bound = bindGarment({
        positions,
        grid: target.grid,
        fallbackBone,
        index: geometry.index ? geometry.index.array : null,
        influencesAt: (v, oi, ow, o) => topInfluences(target.sets[target.owner[v]], target.local[v], GARMENT_INFLUENCES, oi, ow, o),
      })
      geometry.setAttribute('skinIndex', new THREE.BufferAttribute(bound.skinIndex, 4))
      geometry.setAttribute('skinWeight', new THREE.BufferAttribute(bound.skinWeight, 4))

      const mesh = new THREE.SkinnedMesh(geometry, piece.material)
      // The pattern piece's own name is the cloth PATTERN — Marvelous writes
      // Pattern2D_537427 / Shape2D_… on a multi-object export, and that is the
      // "keep the cloth pattern" half of ENG-1108 that survives as structure
      // rather than only as UV. Never renamed, never merged away.
      mesh.name = piece.name || `${name}_piece_${perPiece.length}`
      mesh.userData = { ...piece.userData, isGarment: true, patternPiece: piece.name || null }
      mesh.frustumCulled = false
      // The geometry is baked into the skeleton's SKIN space above, which is
      // precisely the space three's `bindMatrix` is there to reach — so the
      // identity is the whole answer, and the default ATTACHED bind mode then
      // keeps `bindMatrixInverse` equal to the inverse of this mesh's own
      // `matrixWorld` every frame, exactly as it does for the body.
      //
      // Deriving it from the body mesh instead (`ref.bindMatrix x
      // inverse(ref.matrixWorld)`) is what shipped first, and it was a hundredfold
      // error on myra: her meshes hang under an `Armature` scaled 0.01 while her
      // geometry is already in metres, so that product came out at 100x and a
      // 1.47 m garment rendered as a 147 m sheet. It only ever looked right
      // because aoi, danielle and isaac carry their mesh nodes at the identity.
      // Worse, it cannot be right in general: the factor it applies belongs to
      // the BODY's node while `bindMatrixInverse` is driven by the GARMENT's,
      // and the two are different objects.
      mesh.bind(skeleton, new THREE.Matrix4())
      group.add(mesh)

      boundTotal += bound.stats.bound
      unboundTotal += bound.stats.unbound
      worstDist = Math.max(worstDist, bound.stats.maxDist)
      perPiece.push({ name: mesh.name, vertices: bound.stats.points, ...bound.stats })
    }

    root.add(group)
    const hidden = hideOutfit ? hideOwnOutfit(root, outfitPatterns) : []
    if (hidden.length) console.info(`[garment] hid the rig's own outfit: ${hidden.map((m) => m.name).join(', ')}`)

    // Stop the skin coming through the cloth. Done AFTER the garment is placed,
    // because it is decided from where the two surfaces actually sit.
    const culled = cullSkin
      ? cullCoveredSkin(target, group.children.filter((c) => c.isSkinnedMesh))
      : { meshes: [], removed: 0, total: 0 }
    if (culled.removed) {
      console.info(`[garment] hid ${culled.removed} of ${culled.total} body triangles under the garment `
        + `(${((culled.removed / Math.max(culled.total, 1)) * 100).toFixed(1)}%) across `
        + `${culled.meshes.map((m) => m.name).join(', ')}`)
    }

    _attached = { group, hidden, culled: culled.meshes }
    const total = boundTotal + unboundTotal
    // An unbound vertex still renders — it just rides one bone rigidly and
    // tears away the moment the skeleton moves. Say how many, always.
    const unboundPct = total ? (unboundTotal / total) * 100 : 0
    const msg = `[garment] "${name}": ${pieces.length} pattern pieces, ${total} vertices, `
      + `${unboundTotal} unbound (${unboundPct.toFixed(1)}%), furthest bind ${(worstDist * 100).toFixed(1)} cm`
    ;(unboundPct > 5 ? console.warn : console.info)(msg)

    const next = {
      phase: 'ready',
      name, url,
      pieces: pieces.length,
      vertices: total,
      bound: boundTotal,
      unbound: unboundTotal,
      unboundPct,
      worstBindCm: worstDist * 100,
      units,
      fit,
      skin: {
        meshes: target.meshes.map((m) => m.name),
        bones: skeleton.bones.length,
        coverage,
        meanSampleCm: meanDist * 100,
        considered: targets.length,
        scores,
      },
      appliedFit: applyFit,
      hiddenMeshes: hidden.map((m) => m.name),
      culledTriangles: culled.removed,
      culledMeshes: culled.meshes.map((m) => m.name),
      perPiece,
      source,
      assetId,
      // A garment loaded from its server row IS saved; one read out of the
      // local dev directory is not. Saying 'not saved' about a persisted
      // asset is the same class of lie as the reverse, so this follows the
      // source rather than being hardcoded.
      persisted: source === 'asset',
    }
    setState(next)
    return next
  } catch (err) {
    // Loud, and with the name in it: a garment that silently fails to appear is
    // indistinguishable from one that was never asked for.
    console.error(`[garment] "${name}" failed to load from ${url}:`, err)
    const next = { phase: 'error', name, url, error: err?.message ?? String(err) }
    setState(next)
    return next
  }
}

/** Remove the attached garment and restore whatever it hid. */
export function disposeGarment(root) {
  if (!_attached) return
  const { group, hidden, culled } = _attached
  for (const m of hidden) m.visible = true
  // Put back the skin the garment was hiding — the cull belongs to the garment.
  for (const m of culled ?? []) {
    const original = m.userData.garmentCullOriginalIndex
    if (original === undefined) continue
    m.geometry.setIndex(original)
    delete m.userData.garmentCullOriginalIndex
  }
  group.traverse((o) => {
    if (!o.isMesh) return
    o.geometry?.dispose?.()
  })
  ;(root ?? group.parent)?.remove?.(group)
  group.parent?.remove?.(group)
  _attached = null
  setState({ phase: 'idle' })
}

if (typeof window !== 'undefined') {
  window.__GARMENT__ = {
    get state() { return _state },
    get group() { return _attached?.group ?? null },
    load: (root, args) => loadGarment(root, args),
    dispose: (root) => disposeGarment(root),
    onChange: onGarmentChange,
  }
}
